import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { ModulesService } from '../../../api/services';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { UtilityService } from '../../services/utility.service';
import type { Entry, HistoryEntry, Module } from '../../../features/admin/modules/modules.model';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';

@Injectable({
  providedIn: 'root',
})
export class HistoryService {
  private modulesStore = inject(ModulesStore);
  private modulesService = inject(ModulesService);

  /**
   * Fetches history for a specific entry or module
   * @param moduleId - ID of the module
   * @param entryId - ID of the entry (optional)
   */
  getHistory(moduleId: Module['id'], entryId?: Entry['id']): Observable<HistoryEntry[]> {
    const module = this.modulesStore.getModuleById(moduleId);
    const currentEntry = entryId
      ? this.modulesStore.entries()[moduleId]?.find(entry => entry.id === entryId)
      : undefined;

    if (entryId === undefined) {
      return this.modulesService
        .getModuleChangeLog({
          moduleId: moduleId,
        })
        .pipe(
          map((response: any) => {
            try {
              response = JSON.parse(response);
            } catch {}
            return response as HistoryEntry[];
          }),
          map((history: HistoryEntry[]) => {
            return this.processHistoryEntries(history, module, currentEntry);
          })
        );
    } else {
      return this.modulesService
        .getModuleEntryChangeLog({
          moduleId: moduleId,
          entryId: entryId,
        })
        .pipe(
          map((response: any) => {
            try {
              response = JSON.parse(response);
            } catch {}
            return response as HistoryEntry[];
          }),
          map((history: HistoryEntry[]) => {
            return this.processHistoryEntries(history, module, currentEntry);
          })
        );
    }
  }

  /**
   * Process history entries to reconstruct full entry states and clean up unnecessary fields
   */
  private processHistoryEntries(history: HistoryEntry[], module: Module, currentEntry?: Entry): HistoryEntry[] {
    // Sort history by timestamp to ensure chronological order (newest first)
    const sortedHistory = UtilityService.sortArrayByProperty([...history], 'changeTimestamp').reverse();

    // First pass: Reconstruct full entries (preserve all data for fullEntry)
    const reconstructedHistory = this.reconstructFullEntries(sortedHistory, currentEntry ? currentEntry : module);

    // Second pass: Clean up the history entries (only for display purposes)
    return this.cleanHistoryEntries(reconstructedHistory, module);
  }

  /**
   * Clean history entries by removing unnecessary fields and enhancing changes
   * Note: This only cleans the changes array for display purposes, fullEntry remains intact
   */
  private cleanHistoryEntries(history: HistoryEntry[], module: Module): HistoryEntry[] {
    return history
      .map(entry => {
        const entryWithChanges = { ...entry };
        const removeFullPath = ['createdAt', 'updatedAt'];
        const removeContainsKey = ['.entry.'];
        const removeKeys = [
          'uiState',
          '_etag',
          'id',
          'createdByUserId',
          'createdByEmail',
          'partitionKey',
          'createdAt',
          'updatedAt',
          'updatedByUserId',
          'linkedEntries',
        ];
        const removeTaxonomyOrModuleFields: string[] = [];

        // Extract user info from changes
        entryWithChanges.changes.forEach(change => {
          if (change.fieldPath === 'createdByUserId' || change.fieldPath === 'updatedByUserId') {
            const userId = change.to;
            const user = this.modulesStore.entries()[module.id]?.find(entry => {
              return entry.id === userId;
            });
            entryWithChanges.updatedBy = user?.selectedInput ?? entry?.fullEntry?.updatedByEmail ?? userId;
          }
        });

        // Filter out unnecessary fields
        entryWithChanges.changes = entryWithChanges.changes.filter(change => {
          return !removeFullPath.includes(change.fieldPath);
        });

        entryWithChanges.changes = entryWithChanges.changes.filter(change => {
          const keys = change.fieldPath.split('.');
          const fieldKey = keys[keys.length - 1];
          if (change.fieldPath.includes('selectedInput')) {
            change.fieldPath = change.fieldPath.replace('.selectedInput', '');
            removeTaxonomyOrModuleFields.push(change.fieldPath + '.');
          }
          return !removeKeys.includes(fieldKey) && !removeContainsKey.some(key => change.fieldPath.includes(key));
        });

        entryWithChanges.changes = entryWithChanges.changes.filter(change => {
          return !removeTaxonomyOrModuleFields.some(path => change.fieldPath.includes(path));
        });

        // Enhance changes with field info and parse JSON values
        entryWithChanges.changes = entryWithChanges.changes.map(change => {
          change.field = FormHelper.getFieldByKeyPath(
            change.fieldPath,
            this.modulesStore.generatedForms()[module.id],
            true,
            this.modulesStore.generatedForms(),
            this.modulesStore.moduleCodeMapper()
          );

          // Try to parse JSON values
          try {
            change.from = JSON.parse(change.from);
          } catch {}

          try {
            change.to = JSON.parse(change.to);
          } catch {}

          return change;
        });

        return entryWithChanges;
      })
      .filter(entry => {
        // Remove entries with no changes
        return entry.changes.length > 0;
      });
  }

  /**
   * Reconstruct full entries for each history entry
   */
  private reconstructFullEntries(history: HistoryEntry[], currentEntry?: Entry | Module): HistoryEntry[] {
    if (!currentEntry || history.length === 0) {
      // If no current entry or history, return the history as is
      return history;
    }

    // Create a deep copy of the current entry to start with
    const latestEntry = cloneDeep(currentEntry);

    // Process history entries in reverse chronological order (newest first)
    const processedHistory = [...history].map((historyEntry, index) => {
      // Clone the current state before modifications
      const entryAtThisPoint = cloneDeep(latestEntry);

      // Set the full entry in the history entry (preserve ALL data)
      historyEntry.fullEntry = entryAtThisPoint;

      // If not the last entry in history, update latestEntry for next iteration
      if (index < history.length - 1) {
        // Apply the changes in reverse to get the previous state
        historyEntry.changes.forEach(change => {
          if (change.fieldPath && change.from !== undefined) {
            try {
              // Revert to the previous state by applying the 'from' value
              let value = change.from;
              try {
                if (Number(value)) {
                } else {
                  value = JSON.parse(value);
                }
              } catch {
                // If parsing fails, keep the original value
              }

              UtilityService.setKeyValuePath(latestEntry as Entry, change.fieldPath, value);
            } catch (error) {
              console.warn(`Error reverting change for path: ${change.fieldPath}`, error);
            }
          }
        });
      }

      return historyEntry;
    });

    // Add logic to reconstruct the initial state (creation)
    if (processedHistory.length > 0) {
      const lastHistoryEntry = processedHistory[processedHistory.length - 1];

      // Build the creation state by applying all remaining changes in reverse
      const initialState = cloneDeep(lastHistoryEntry.fullEntry);

      lastHistoryEntry.changes.forEach(change => {
        // For creation events, 'from' is often null or undefined, so we need to clear the field values
        if (change.fieldPath) {
          try {
            if (change.from === undefined || change.from === null) {
              // If from is undefined/null, try to remove the property
              const pathParts = change.fieldPath.split('.');
              const lastPart = pathParts.pop();

              // Get the parent object
              if (pathParts.length > 0) {
                const parentObj = pathParts.reduce((obj, part) => obj?.[part], initialState);
                if (parentObj && lastPart in parentObj) {
                  delete parentObj[lastPart];
                }
              } else if (lastPart in initialState) {
                delete initialState[lastPart];
              }
            } else {
              // Otherwise set to the 'from' value
              UtilityService.setKeyValuePath(initialState as Entry, change.fieldPath, change.from);
            }
          } catch (error) {
            console.warn(`Error reconstructing initial state for path: ${change.fieldPath}`, error);
          }
        }
      });

      // Add a synthetic entry for the initial creation state
      if (this.isCreationEvent(lastHistoryEntry)) {
        const creationEntry: HistoryEntry = {
          updatedBy: lastHistoryEntry.updatedBy,
          changeTimestamp: new Date(new Date(lastHistoryEntry.changeTimestamp).getTime() - 1000).toISOString(), // 1 second before
          fullEntry: initialState,
          changes: lastHistoryEntry.changes.map(change => ({
            ...change,
            to: change.from,
            from: undefined,
          })),
        };

        processedHistory.push(creationEntry);
      }
    }

    return processedHistory;
  }

  /**
   * Check if a history entry represents a creation event
   */
  private isCreationEvent(entry: HistoryEntry): boolean {
    return entry.changes.some(change => change.fieldPath === 'id' && !change.from && change.to);
  }

  /**
   * Restore an entry to a specific point in its history
   * @param moduleId - ID of the module
   * @param historyEntry - The history entry containing the full state to restore
   * @param entryId - ID of the entry to restore (optional)
   * @returns Observable of the updated entry
   */
  restoreToVersion(moduleId: Module['id'], historyEntry: HistoryEntry, entryId?: Entry['id']) {
    if (!historyEntry.fullEntry) {
      throw new Error('Cannot restore: History entry does not contain full entry state');
    }
    // TODO FIX RESTORE WITH ARRAY

    if (entryId) {
      return this.modulesStore.editEntry({
        moduleId: moduleId,
        entryId: entryId,
        entry: historyEntry.fullEntry as Entry,
        showSnackBarMessage: false,
      });
    } else {
      return this.modulesStore.edit({
        id: moduleId,
        data: historyEntry.fullEntry as Module,
      });
    }
  }
}
